/**
 * BootScreen — the loading screen shown for every BootPhase except 'ready'.
 *
 * Fully modal: renders instead of the chat UI, never alongside it, so
 * there is no interactive surface (input, sidebar) a user could touch
 * mid-load. See startup-sequencing spec §3.
 *
 * No pacing: this component has no internal timers or animation-driven
 * transitions of its own — it renders whatever BootPhase it's given, the
 * instant it's given it. The only "wait" a user perceives is the actual
 * network latency of the phase in progress.
 */
import React from 'react';
import type { BootPhase, BootStep } from './useBootSequence';

/**
 * Phase-specific flavor text, in the voice of Mr. Pibb hosting a guest and
 * pouring their drink. Kept as a standalone constant (not inlined in JSX)
 * so copy changes don't require touching render logic.
 */
export const BOOT_PHASE_TAGLINES: Record<BootStep, string> = {
  authenticating: 'Setting your table...',
  loadingWorkspace: 'Finding your favorite mug...',
  openingConversation: 'Pouring you a fresh drink...',
};

interface BootScreenProps {
  bootPhase: BootPhase;
  onRetry: () => void;
}

export function BootScreen({ bootPhase, onRetry }: BootScreenProps): React.ReactElement {
  const tagline = bootPhase.phase !== 'error' && bootPhase.phase !== 'ready'
    ? BOOT_PHASE_TAGLINES[bootPhase.phase]
    : undefined;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '16px',
      textAlign: 'center',
    }}>
      <style>{`
        @keyframes boot-fizz {
          0%, 100% { opacity: 0.2; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-4px); }
        }
      `}</style>
      <img src="/MrPibbAMA.png" alt="Mr. Pibb" style={{ height: '120px' }} />
      <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Mr. Pibb</div>
      {bootPhase.phase === 'error' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '14px', color: '#c62828' }}>{bootPhase.message}</div>
          <button
            onClick={onRetry}
            style={{
              padding: '8px 20px',
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <div style={{ fontSize: '14px', color: '#666' }}>
          {tagline}
          <span style={{ display: 'inline-block', letterSpacing: '2px', marginLeft: '6px' }}>
            <span style={{ animation: 'boot-fizz 1.4s infinite', animationDelay: '0s' }}>°</span>
            <span style={{ animation: 'boot-fizz 1.4s infinite', animationDelay: '0.2s' }}>°</span>
            <span style={{ animation: 'boot-fizz 1.4s infinite', animationDelay: '0.4s' }}>•</span>
          </span>
        </div>
      )}
    </div>
  );
}
